import classNames from 'classnames';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {useI18n} from '@udemy/i18n';
import {useViewport, withinOrWiderThan} from '@udemy/react-layout-components';
import {ShowMore} from '@udemy/react-reveal-components';
import {
    Carousel,
    CarouselMutationEvent,
    CarouselNextButton,
    CarouselPreviousButton,
    CarouselProps,
    dispatchCarouselNextPageEvent,
    dispatchCarouselPreviousPageEvent,
} from '@udemy/react-structure-components';
import {SlideIndicatorDots} from '@udemy/shared-one-udemy-components';

import styles from './enriched-skills.module.less';

interface EnrichedSkillsProps extends Pick<CarouselProps, 'onPreviousClick' | 'onNextClick'> {
    /**
     * The heading element to use for the heading.
     *
     * @defaultValue 'h3'
     */
    headingElement?: 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    /**
     * The title of the career life skills section.
     *
     * @defaultValue 'Learn essential career and life <span style="color: var(--color-purple-300);">skills</span>'
     */
    title?: string;
    /**
     * The subtitle of the career life skills section.
     *
     * @defaultValue 'Our platform delivers, AI-powered skills acceleration to help you build in-demand skills fast and advance your career in a changing job market.'
     */
    subtitle?: string;
    /**
     * Event handler for the previous button on desktop carousel
     */
    onPreviousClick?: () => void;
    /**
     * Event handler for the next button on desktop carousel
     */
    onNextClick?: () => void;
    /**
     * The children of the career life skills section.
     */
    children: React.ReactNode;
}

export const EnrichedSkills = ({
    headingElement = 'h3',
    title,
    subtitle,
    onPreviousClick,
    onNextClick,
    children,
}: EnrichedSkillsProps) => {
    const {gettext} = useI18n();
    const [pagerEventTarget, setPagerEventTarget] = useState<EventTarget>();
    const [disableNextButton, setDisableNextButton] = useState(false);
    const [disablePrevButton, setDisablePrevButton] = useState(false);
    const [visiblePageIndex, setVisiblePageIndex] = useState(0);
    const previousPageIndexRef = useRef<number>(0);
    const {range} = useViewport();

    const decodeHtmlEntities = (text: string) => {
        const textarea = document.createElement('textarea');
        textarea.innerHTML = text;
        return textarea.value;
    };

    const titleString = decodeHtmlEntities(
        title ??
            gettext(
                'Learn essential career and life <span style="color: var(--color-purple-300);">skills</span>',
            ),
    );
    const subtitleString = decodeHtmlEntities(
        subtitle ??
            gettext(
                'Our platform delivers, AI-powered skills acceleration to help you build in-demand skills fast and advance your career in a changing job market.',
            ),
    );

    const HeadingElement = headingElement;
    const isDesktop = range && withinOrWiderThan(range, 'sm');
    const isXl = range && withinOrWiderThan(range, 'xl');
    const itemsVisible = isXl ? 3 : 2;
    const totalItems = React.Children.toArray(children).length;
    const itemsToMove = 2; // Carousel moves 2 items at a time

    // Calculate number of pages based on carousel behavior:
    // - XL: Shows 3 items, moves 2 (preserving 1 from previous page)
    // - Smaller desktop: Shows 2 items, moves 2 (showing 2 new items)
    const slides = useMemo(() => {
        if (itemsVisible === 3) {
            // For XL: (totalItems - itemsVisible) / itemsToMove + 1
            // Example: 9 items -> (9-3)/2 + 1 = 4 pages
            return Math.ceil((totalItems - itemsVisible) / itemsToMove) + 1;
        } else {
            // For smaller desktop: simple division works
            // Example: 9 items -> ceil(9/2) = 5 pages
            return Math.ceil(totalItems / itemsVisible);
        }
    }, [totalItems, itemsVisible, itemsToMove]);

    const handleMutation = (event: CarouselMutationEvent) => {
        // Handle the carousel's reported visiblePageIndex
        // For smaller screens, if the carousel reports indices that jump (e.g., 0 -> 2),
        // we smooth it out by ensuring it only increments/decrements by 1
        let mappedPageIndex: number;
        const reportedIndex = event.visiblePageIndex as number | null | undefined;
        const previousIndex = previousPageIndexRef.current;

        if (reportedIndex !== null && reportedIndex !== undefined) {
            mappedPageIndex = reportedIndex;

            // For smaller screens, if there's a jump (difference > 1), interpolate
            // This handles cases where the carousel reports 0 -> 2 instead of 0 -> 1
            if (itemsVisible === 2 && Math.abs(mappedPageIndex - previousIndex) > 1) {
                // If jumping forward, increment by 1; if jumping backward, decrement by 1
                mappedPageIndex =
                    mappedPageIndex > previousIndex ? previousIndex + 1 : previousIndex - 1;
            }

            // Ensure the index is within valid bounds
            mappedPageIndex = Math.max(0, Math.min(mappedPageIndex, slides - 1));
        } else if (event.isLastPage === true) {
            // If on last page but no index provided, use the last page index
            mappedPageIndex = slides - 1;
        } else {
            // Fallback to previous index or 0
            mappedPageIndex = previousIndex;
        }

        // Update the ref for next comparison
        previousPageIndexRef.current = mappedPageIndex;
        setVisiblePageIndex(mappedPageIndex);

        // Use explicit boolean checks to avoid stale state issues
        // Always set the state based on the event, not the current state
        if (event.isLastPage === true) {
            setDisableNextButton(true);
        } else if (event.isLastPage === false) {
            setDisableNextButton(false);
        }

        // Handle previous button state
        if (mappedPageIndex === 0) {
            setDisablePrevButton(true);
        } else {
            setDisablePrevButton(false);
        }
    };

    const customNextClick = () => {
        dispatchCarouselNextPageEvent(pagerEventTarget as EventTarget);
    };
    const customPrevClick = () => {
        dispatchCarouselPreviousPageEvent(pagerEventTarget as EventTarget);
    };

    useEffect(() => {
        // `EventTarget` is not supported nodeJS 14.*, so instantiate it client-side in browser.
        setPagerEventTarget(new EventTarget());
    }, []);

    // Reset button states when children or viewport changes (which affects slide count)
    useEffect(() => {
        // Reset button states when slides change to avoid stale disabled state
        setDisableNextButton(false);
        setDisablePrevButton(false);
        setVisiblePageIndex(0);
        previousPageIndexRef.current = 0;
    }, [children, isXl, slides]);

    const renderDektopCarousel = () => (
        <>
            <Carousel
                data-testid="carousel"
                className={styles['carousel-9-column']}
                showPager={false}
                onMutation={handleMutation}
                onNextClick={onNextClick}
                onPreviousClick={onPreviousClick}
                pagerEventTarget={pagerEventTarget}
            >
                {children}
            </Carousel>
            <div className={styles['pager-buttons-wrapper']}>
                <CarouselPreviousButton
                    data-testid="prev-button"
                    pagerButtonSize="medium"
                    className={classNames(styles['slide-indicator-pager'], {
                        [styles.disabled]: disablePrevButton,
                    })}
                    disabled={disablePrevButton}
                    onClick={customPrevClick}
                    udStyle={'white-solid'}
                />
                <SlideIndicatorDots pageCount={slides} visiblePageIndex={visiblePageIndex} />
                <CarouselNextButton
                    pagerButtonSize="medium"
                    className={classNames(styles['slide-indicator-pager'], {
                        [styles.disabled]: disableNextButton,
                    })}
                    disabled={disableNextButton}
                    onClick={customNextClick}
                    udStyle={'white-solid'}
                />
            </div>
        </>
    );

    const renderMobileCards = () => (
        <ShowMore data-testid="show-more" collapsedHeight={824}>
            <div className={styles['mobile-card-grid']}>{children}</div>
        </ShowMore>
    );

    return (
        <section
            className={classNames('ud-full-viewport-container', styles['enriched-skills-wrapper'])}
        >
            <div className="ud-container">
                <div className={styles['layouts-for-cards']}>
                    <div className={classNames(styles['layout-12-column-container'])}>
                        <div className={styles['placeholder-3-column']}>
                            <HeadingElement
                                className={classNames(styles.heading, 'ud-heading-xxl')}
                                dangerouslySetInnerHTML={{__html: titleString}}
                            ></HeadingElement>
                            <p
                                className={classNames(styles.description, 'ud-text-md')}
                                dangerouslySetInnerHTML={{__html: subtitleString}}
                            />
                        </div>
                        <div className={styles['carousel-9-column-container']}>
                            {isDesktop && renderDektopCarousel()}
                            {!isDesktop && renderMobileCards()}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
